import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";

export function PdfPreviewModal({ open, onClose, path, filename, title }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let img = null;
    let pdf = null;
    if (open && path) {
      setLoading(true);
      setImgUrl(null);
      setPdfUrl(null);
      const sep = path.includes("?") ? "&" : "?";
      // Image render = reliable fit-to-width preview on any device; PDF blob = for download.
      api
        .get(`${path}${sep}img=1`, { responseType: "blob" })
        .then((res) => {
          img = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
          setImgUrl(img);
        })
        .catch(() => toast.error("Could not load the preview"))
        .finally(() => setLoading(false));
      api
        .get(path, { responseType: "blob" })
        .then((res) => {
          pdf = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          setPdfUrl(pdf);
        })
        .catch(() => {});
    }
    return () => {
      if (img) URL.revokeObjectURL(img);
      if (pdf) URL.revokeObjectURL(pdf);
    };
  }, [open, path]);

  const download = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = filename || "document.pdf";
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent
        className="bg-[#0f0f0f]/95 backdrop-blur-xl border-white/10 text-white w-screen h-[100dvh] max-w-none sm:w-[97vw] sm:max-w-4xl sm:h-auto sm:max-h-[95vh] p-0 overflow-hidden rounded-none sm:rounded-lg flex flex-col"
        data-testid="pdf-preview-modal"
      >
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0">
          <DialogTitle className="font-display text-xl tracking-tight pr-8">{title || "Document preview"}</DialogTitle>
          <DialogDescription className="sr-only">Document preview. Use the Download button to save the PDF.</DialogDescription>
        </DialogHeader>
        <div className="px-3 sm:px-5 pb-3 flex-1 min-h-0">
          <div className="rounded-xl border border-white/10 bg-black/60 h-full min-h-[60vh] sm:h-[78vh] overflow-y-auto flex items-start justify-center">
            {loading || !imgUrl ? (
              <div className="h-full min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#ff3b30]" />
              </div>
            ) : (
              <img src={imgUrl} alt="Document preview" className="w-full h-auto block" data-testid="pdf-preview-frame" />
            )}
          </div>
        </div>
        <div className="px-5 pb-5 flex justify-end shrink-0">
          <Button
            onClick={download}
            disabled={!pdfUrl}
            data-testid="pdf-preview-download"
            className="h-11 bg-white text-black hover:bg-white/90 font-semibold rounded-xl"
          >
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
